'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

// --- Configuration ---
// Note: Ensure your phone number is correct (removed 'f' typo if present)
const PHONE_NUMBER = '923460442929'; 
const PRE_FILLED_MESSAGE = 'Hi Aura Team, I am interested in the transformation program.';

// Official WhatsApp Brand Colors
const WA_GREEN = '#25D366';      // Standard logo green
const WA_DARK_GREEN = '#128C7E'; // Darker teal for gradients/depth

export default function WhatsAppWidget() {
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    // Show widget after 2 seconds
    const timer = setTimeout(() => {
      setIsVisible(true);
      setShowTooltip(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const whatsappLink = `https://wa.me/${PHONE_NUMBER}?text=${encodeURIComponent(PRE_FILLED_MESSAGE)}`;

  // ==========================================
  // 1. PIXEL TRACKING HANDLER
  // ==========================================
  const handleWhatsAppClick = () => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Contact', {
        content_name: 'WhatsApp Inquiry',
        contact_method: 'WhatsApp'
      });
      console.log("✅ Pixel Event: Contact (WhatsApp) Fired");
    }
  };
  // ==========================================

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed bottom-6 left-6 z-[9999] flex flex-col items-start gap-2">
          
          {/* --- Tooltip --- */}
          <AnimatePresence>
            {showTooltip && (
              <motion.div
                initial={{ opacity: 0, x: -20, scale: 0.8 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
                className="relative bg-white text-black px-4 py-2 rounded-xl shadow-xl border border-[#25D366]/30 max-w-[200px]"
              >
                <div className="flex justify-between items-start gap-2">
                    <p className="text-sm font-bold leading-tight">
                        Ready to Transform? <br/>
                        <span className="text-xs font-normal text-gray-600">Chat with us.</span>
                    </p>
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowTooltip(false);
                        }}
                        className="text-gray-400 hover:text-black transition-colors"
                    >
                        <X size={14} />
                    </button>
                </div>
                
                <div className="absolute -bottom-2 left-5 w-4 h-4 bg-white transform rotate-45 border-b border-r border-[#25D366]/30" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* --- The WhatsApp Button --- */}
          <motion.a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            // 2. ATTACH THE HANDLER HERE
            onClick={handleWhatsAppClick} 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onMouseEnter={() => setShowTooltip(true)}
            className="relative group flex items-center justify-center w-14 h-14 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.4)] cursor-pointer"
            style={{
              background: `linear-gradient(135deg, ${WA_GREEN}, ${WA_DARK_GREEN})`,
            }}
          >
            {/* Pulse Effect */}
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-20 animate-ping duration-1000" />
            
            {/* Icon */}
            <svg
              viewBox="0 0 24 24"
              fill="white"
              className="w-8 h-8 relative z-10"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </motion.a>
        </div>
      )}
    </AnimatePresence>
  );
}